// src/components/ui.tsx — shared heritage primitives. A small Phase-0 subset of
// the prototype bp-ui.jsx; the full icon/primitive set is ported as the UI
// phases land. All styling is inline objects keyed off theme tokens.

import type { CSSProperties, ReactNode } from "react";
import { C, F } from "../theme";
import type { Role } from "../auth";
import { roleLabel } from "../auth";

const ROLE_TONE: Record<Role, { fg: string; bg: string; bd: string }> = {
  admin:      { fg: C.brassDk,   bg: C.brassBg,  bd: "rgba(167,117,28,0.30)" },
  lineadmin:  { fg: C.heatherDk, bg: "#ECE0F2",  bd: "rgba(122,93,140,0.30)" },
  lineviewer: { fg: C.heatherDk, bg: "#F1EAF6",  bd: "rgba(122,93,140,0.22)" },
  editor:     { fg: C.sageDk,    bg: "#E4ECDF",  bd: "rgba(110,140,114,0.30)" },
  viewer:     { fg: C.inkSoft,   bg: C.paperWarm, bd: C.hairline },
};

export function RolePill({ role }: { role: Role }) {
  const t = ROLE_TONE[role] ?? ROLE_TONE.viewer;
  return (
    <span style={{
      fontFamily: F.mono, fontSize: 11, fontWeight: 700, letterSpacing: 0.3,
      textTransform: "uppercase", color: t.fg, background: t.bg,
      border: `1px solid ${t.bd}`, borderRadius: 999, padding: "3px 10px", whiteSpace: "nowrap",
    }}>
      {roleLabel(role)}
    </span>
  );
}

export function Pill({ children, tone = "ink" }: { children: ReactNode; tone?: "ink" | "brass" | "sage" }) {
  const tones = {
    ink:   { fg: C.inkSoft, bg: C.paperWarm, bd: C.hairline },
    brass: { fg: C.brassDk, bg: C.brassBg, bd: "rgba(167,117,28,0.30)" },
    sage:  { fg: C.sageDk, bg: "#E4ECDF", bd: "rgba(110,140,114,0.30)" },
  }[tone];
  return (
    <span style={{
      fontFamily: F.mono, fontSize: 11, fontWeight: 700, color: tones.fg, background: tones.bg,
      border: `1px solid ${tones.bd}`, borderRadius: 999, padding: "3px 9px",
    }}>{children}</span>
  );
}

export function Btn({
  children, onClick, disabled, variant = "default", title, style,
}: {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  variant?: "default" | "primary" | "ghost";
  title?: string;
  style?: CSSProperties;
}) {
  const base: CSSProperties = {
    fontFamily: F.body, fontSize: 13, fontWeight: 700, borderRadius: 9,
    padding: "8px 14px", cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.5 : 1, transition: "background 120ms, border-color 120ms",
    border: `1px solid ${C.hairline}`, background: C.card, color: C.inkSoft,
  };
  const variants: Record<string, CSSProperties> = {
    default: {},
    primary: { background: C.brass, borderColor: C.brassDk, color: C.inkOnDark },
    ghost:   { background: "transparent", borderColor: "transparent", color: C.inkSoft },
  };
  return (
    <button type="button" onClick={onClick} disabled={disabled} title={title}
      style={{ ...base, ...variants[variant], ...style }}>
      {children}
    </button>
  );
}

const spinnerStyle = `@keyframes bp-spin { to { transform: rotate(360deg); } }`;

export function LoadingScreen({ label = "Loading…" }: { label?: string }) {
  return (
    <div style={{
      minHeight: "100vh", display: "flex", flexDirection: "column", gap: 18,
      alignItems: "center", justifyContent: "center", background: C.paper,
    }}>
      <style>{spinnerStyle}</style>
      <div style={{ fontFamily: F.serif, fontSize: 26, fontWeight: 700, color: C.brassDk, letterSpacing: 0.4 }}>
        The Big Plan
      </div>
      <div style={{
        width: 34, height: 34, borderRadius: "50%",
        border: `3px solid ${C.hairline}`, borderTopColor: C.brass,
        animation: "bp-spin 0.8s linear infinite",
      }} />
      <div style={{ fontFamily: F.body, fontSize: 13, color: C.inkMute }}>{label}</div>
    </div>
  );
}

export function ErrorScreen({ message }: { message: string }) {
  return (
    <div style={{
      minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: C.paper, padding: 24,
    }}>
      <div style={{
        maxWidth: 460, background: C.card, border: `1px solid ${C.signalBg}`,
        borderLeft: `4px solid ${C.signal}`, borderRadius: 12, padding: "22px 24px", boxShadow: C.shadowM,
      }}>
        <div style={{ fontFamily: F.serif, fontSize: 19, fontWeight: 700, color: C.signalDk, marginBottom: 8 }}>
          Something went wrong
        </div>
        <div style={{ fontFamily: F.body, fontSize: 14, color: C.inkSoft, lineHeight: 1.5 }}>{message}</div>
        <div style={{ marginTop: 14 }}>
          <Btn onClick={() => window.location.reload()}>Reload</Btn>
        </div>
      </div>
    </div>
  );
}
